import { useEffect, useRef, useState } from 'react';
import { urlFotoFicha } from '@/lib/foto';

interface CampoFotoProps {
  arquivo: File | null;
  erro: string | null;
  enviando: boolean;
  enviada: boolean;
  aoSelecionar: (arquivo: File | null) => void;
  // Caminho de uma foto já salva no bucket 'fichas' (opcional).
  // Quando presente, ela aparece pré-carregada.
  fotoExistente?: string | null;
  // Foto de outra pessoa: abre a câmera de trás. A própria abre a da frente.
  cameraTraseira?: boolean;
}

export default function CampoFoto({
  arquivo,
  erro,
  enviando,
  enviada,
  aoSelecionar,
  fotoExistente,
  cameraTraseira,
}: CampoFotoProps) {
  const inputCameraRef = useRef<HTMLInputElement>(null);
  const inputGaleriaRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [fotoExistenteUrl, setFotoExistenteUrl] = useState<string | null>(null);

  // Miniatura local: aparece na hora, sem esperar o envio terminar.
  useEffect(() => {
    if (!arquivo) {
      setPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(arquivo);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [arquivo]);

  useEffect(() => {
    let ativo = true;
    setFotoExistenteUrl(null);
    if (!arquivo && fotoExistente) {
      urlFotoFicha(fotoExistente).then((url) => {
        if (ativo) setFotoExistenteUrl(url);
      });
    }
    return () => {
      ativo = false;
    };
  }, [fotoExistente, arquivo]);

  function aoEscolher(e: React.ChangeEvent<HTMLInputElement>) {
    const arquivoNovo = e.target.files?.[0] ?? null;
    if (arquivoNovo) aoSelecionar(arquivoNovo);
    e.target.value = '';
  }

  const urlVisivel = preview ?? fotoExistenteUrl;

  return (
    <div>
      <div className="flex min-h-[240px] w-full flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-secondary-300 bg-background-50 px-4 py-6">
        {urlVisivel ? (
          <>
            <div className="relative">
              <img
                src={urlVisivel}
                alt="Prévia da foto"
                className="max-h-[240px] w-auto max-w-full rounded-md object-contain"
              />
              {enviando && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-md bg-foreground-950/50">
                  <span className="h-7 w-7 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                </div>
              )}
            </div>

            {enviando ? (
              <p className="flex items-center gap-1.5 text-sm font-medium text-secondary-600">
                <i className="ri-upload-cloud-line text-base" aria-hidden />
                Enviando foto…
              </p>
            ) : enviada ? (
              <p className="flex items-center gap-1.5 text-sm font-medium text-green-600">
                <i className="ri-checkbox-circle-fill text-base" aria-hidden />
                Foto enviada
              </p>
            ) : (
              <p className="text-center text-sm text-secondary-500">
                {preview ? 'A foto será usada na sua inscrição.' : 'Usaremos esta foto'}
              </p>
            )}
          </>
        ) : (
          <>
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-400">
              <i className="ri-camera-line text-3xl" aria-hidden />
            </span>
            <p className="text-center text-sm text-secondary-500">
              Uma foto do rosto ajuda a equipe a identificar você.
            </p>
          </>
        )}

        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => inputCameraRef.current?.click()}
            className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600"
          >
            {urlVisivel ? 'Tirar outra foto' : 'Tirar a foto'}
          </button>
          <button
            type="button"
            onClick={() => inputGaleriaRef.current?.click()}
            className="cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            Escolher foto
          </button>
          {urlVisivel && (
            <button
              type="button"
              onClick={() => aoSelecionar(null)}
              className="cursor-pointer whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium text-danger-600 transition-colors hover:bg-danger-50"
            >
              Remover foto
            </button>
          )}
        </div>
      </div>

      {/* A própria pessoa usa a câmera frontal (foto de rosto).
          "Outra pessoa" usa a de trás (capture="environment").
          No computador o atributo é ignorado e abre o seletor de arquivos. */}
      <input
        ref={inputCameraRef}
        type="file"
        accept="image/*"
        capture={cameraTraseira ? 'environment' : 'user'}
        className="hidden"
        onChange={aoEscolher}
      />
      <input
        ref={inputGaleriaRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={aoEscolher}
      />

      {erro && (
        <p className="mt-1 text-sm text-danger-600" role="alert" data-erro="true">
          {erro}
        </p>
      )}
    </div>
  );
}